import { z } from 'zod/v3';

// ============================================================================
// Enum 스키마
// ============================================================================

/** E쿠폰 상품 구분 (무상포인트전용 등) */
export const eCouponProductTypeSchema = z.enum(['NORMAL', 'FREE_POINT_ONLY']);
export type ECouponProductType = z.infer<typeof eCouponProductTypeSchema>;

// ============================================================================
// 서브 스키마
// ============================================================================

/** 모바일 교환권 정보 */
export const marketECouponInfoSchema = z.object({
  /** 제휴사 ID */
  brandId: z.number(),
  /** 제휴사명 */
  brandName: z.string(),
  /** 쿠폰 할인 허용 여부 */
  couponAllowed: z.boolean(),
  /** E쿠폰 상품 구분 (무상포인트전용 등) */
  eCouponProductType: eCouponProductTypeSchema,
  /** 선물하기 허용 여부 */
  giftAllowed: z.boolean(),
  /** 제휴사 아이콘 URL */
  iconImageUrl: z.string().optional(),
  /** 멤버십 할인 허용 여부 */
  membershipDiscountAllowed: z.boolean(),
  /** 쿠폰출력방식 */
  outType: z.string().optional(),
  /** 포인트 사용 허용 여부 */
  pointAllowed: z.boolean(),
  /** 사용장소 */
  useArea: z.string().optional(),
  /** 제한사항 */
  useLimit: z.string().optional(),
  /** 주의사항 */
  useNote: z.string().optional(),
  /** 유효기간 */
  useTerm: z.string().optional(),
});
export type MarketECouponInfo = z.infer<typeof marketECouponInfoSchema>;

/** 상품 정보 제공 고시 항목 */
export const marketProductNoticeItemSchema = z.object({
  /** 생성일, ISO-8601 형식 문자열, UTC(+0) */
  createdAt: z.string().optional(),
  /** 상품정보 제공고시 항목 ID */
  id: z.string().optional(),
  /** 이름 */
  name: z.string().optional(),
  /** 항목 순서번호 */
  orderNumber: z.number(),
  /** 상품정보 제공고지 종류 ID */
  productNoticeTypeId: z.string().optional(),
  /** 수정일, ISO-8601 형식 문자열, UTC(+0) */
  updatedAt: z.string().optional(),
  /** 값 */
  value: z.string().optional(),
});
export type MarketProductNoticeItem = z.infer<
  typeof marketProductNoticeItemSchema
>;

/** 상품정보 제공고시 종류 */
export const marketProductNoticeTypeSchema = z.object({
  /** 생성일, ISO-8601 형식 문자열, UTC(+0) */
  createdAt: z.string().optional(),
  /** 설명 */
  description: z.string().optional(),
  /** 상품정보 제공고시 종류 ID */
  id: z.string().optional(),
  /** 식별 코드 */
  identityCode: z.number().optional(),
  /** 이름 */
  name: z.string().optional(),
  /** 수정일, ISO-8601 형식 문자열, UTC(+0) */
  updatedAt: z.string().optional(),
  /** 버전 */
  version: z.number().optional(),
});
export type MarketProductNoticeType = z.infer<
  typeof marketProductNoticeTypeSchema
>;

/** 상품 정보 제공 고시 정보 */
export const marketProductNoticeSchema = z.object({
  /** 상품 정보 제공 고시 항목 */
  items: z.array(marketProductNoticeItemSchema),
  /** 상품 정보 제공 고시 ID */
  productNoticeId: z.number().optional(),
  /** 상품 정보 제공 고시 종류 정보 */
  productNoticeType: marketProductNoticeTypeSchema,
  /** SKU ID */
  skuId: z.number().optional(),
});
export type MarketProductNotice = z.infer<typeof marketProductNoticeSchema>;

// ============================================================================
// Model 스키마
// ============================================================================

/**
 * 모바일 교환권 상세 응답 스키마
 * @see GET /api/non-login/v1/e-coupon/{eCouponId}
 */
export const eCouponDetailSchema = z.object({
  /** 쿠폰 번호 */
  couponNumber: z.string(),
  /** 남은 잔액 (쿠프 BAL_PRICE) */
  balancePrice: z.number().optional(),
  /** 모바일 교환권 정보 */
  eCouponInfo: marketECouponInfoSchema.optional(),
  /** 유효기간 종료일 */
  expirationDate: z.string().optional(),
  /** 모바일 교환권 이미지 URL */
  imageUrl: z.string().optional(),
  /** 핀 번호 */
  pinNumber: z.string().optional(),
  /** 상품 고시 정보 */
  productNotice: marketProductNoticeSchema.optional(),
});
export type ECouponDetail = z.infer<typeof eCouponDetailSchema>;
